import {Button} from '@wandb/components';
import {getTagContrastColor, Tag} from '@wandb/weave/components/Tag';
import React, {type FC, useState} from 'react';
import {Divider, Input, List, Popup} from 'semantic-ui-react';

import {
  MultiStateCheckbox,
  type MultiStateCheckboxCheckedState,
} from '../MultiStateCheckbox';
import {useRunSelectorAnalyticsLocation} from '../RunSelector/RunSelectorAnalyticsLocationContext';

export interface TagState {
  name: string;
  colorIndex?: number;
  count: number;
}

interface BulkTagEditorProps {
  selectedCount: number;
  tags: TagState[];
  addTag(tag: string): void;
  removeTag(tag: string): void;
}

const DEFAULT_TAGS = ['baseline', 'hidden'];

function countToCheckState(
  count: number,
  selectedCount: number
): MultiStateCheckboxCheckedState {
  if (count === 0) {
    return 'unchecked';
  } else if (count < selectedCount) {
    return 'partial';
  } else {
    return 'checked';
  }
}

const BulkTagEditor: FC<BulkTagEditorProps> = ({
  selectedCount,
  tags,
  addTag,
  removeTag,
}) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const [open, setOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const renderTag = (
    tag: TagState,
    count: number,
    isNew: boolean,
    index: number = 0
  ) => {
    const addOnClick = tag.count === 0 || tag.count < count;
    return (
      <List.Item key={tag.name}>
        <MultiStateCheckbox
          style={{marginRight: '4px'}}
          checked={countToCheckState(tag.count, count)}
          onClick={() => {
            if (addOnClick) {
              addTag(tag.name);
            } else {
              removeTag(tag.name);
            }
          }}
        />
        {isNew && (
          <span
            style={{
              fontSize: '14px',
              paddingTop: '2px',
              marginRight: '4px',
            }}>
            Create new tag:{' '}
          </span>
        )}
        <Tag label={tag.name} color={getTagContrastColor(tag.colorIndex)} />
      </List.Item>
    );
  };

  const renderTags = () => {
    const tagSearchTrimmed = tagSearch.trim();

    const tagsCopy = [...tags];
    DEFAULT_TAGS.forEach(tagName => {
      if (!tagsCopy.find(t => t.name === tagName)) {
        tagsCopy.push({
          name: tagName,
          colorIndex: tagsCopy.length,
          count: 0,
        });
      }
    });

    let filteredTags = tagsCopy;

    if (tagSearchTrimmed) {
      const t = tagSearchTrimmed.toLocaleLowerCase();

      filteredTags = tagsCopy.filter(({name}) =>
        name.toLocaleLowerCase().includes(t)
      );
      filteredTags.sort(({name}) => (name.toLocaleLowerCase() === t ? 0 : 1));
    }

    const newTag =
      tagSearchTrimmed && !tagsCopy.find(t => t.name === tagSearchTrimmed);

    return (
      <List>
        {filteredTags.map((tag, index) =>
          renderTag(tag, selectedCount, false, index)
        )}
        {newTag &&
          renderTag(
            {
              name: tagSearchTrimmed,
              colorIndex: tagsCopy.length,
              count: 0,
            },
            selectedCount,
            true
          )}
      </List>
    );
  };

  // TODO: setup states properly

  const button = (
    <Button
      data-test="tag-popup"
      startIcon="tag"
      isActive={open}
      variant="secondary"
      onPress={() => {
        window.analytics?.track('Tag dropdown clicked', {
          location: analyticsLocation,
        });
      }}>
      Tag
    </Button>
  );

  if (selectedCount === 0) {
    return null;
  }

  return (
    <Popup
      basic
      className="wb-table-action-popup"
      on="click"
      position="bottom left"
      open={open}
      onOpen={() => {
        setOpen(true);
        setTagSearch('');
      }}
      onClose={() => setOpen(false)}
      trigger={button}
      popperModifiers={{
        preventOverflow: {
          // prevent popper from erroneously constraining the popup to the
          // table header
          boundariesElement: 'viewport',
        },
      }}
      content={
        <div className="tag-popup">
          {selectedCount === 0 ? (
            <div className="title">Select runs in the table to apply tags</div>
          ) : (
            <React.Fragment>
              <div className="title">
                {`Tag ${selectedCount} selected ${
                  selectedCount === 1 ? 'run' : 'runs'
                }:`}
              </div>
              <Input
                className="tag"
                icon="search"
                placeholder="Search or create a tag"
                value={tagSearch}
                onChange={(e, {value}) => setTagSearch(value.substring(0, 64))}
              />
              <Divider fitted />
              {renderTags()}
            </React.Fragment>
          )}
        </div>
      }
    />
  );
};

export default BulkTagEditor;
